import moment from "moment";

// Shared constants
export const FREQUENCY_TIME_UNIT_OPTIONS = [
  { label: "Days", value: "DAYS" },
  { label: "Hours", value: "HOURS" },
];

export const ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS = [
  { label: "amazon.com", value: "AMAZON_US" },
  { label: "Fresh Stores US", value: "FRESH_STORES_US" },
  { label: "Whole Foods Market US", value: "WHOLE_FOODS_MARKET_US" },
  { label: "amazon.ca", value: "AMAZON_CA" },
  { label: "amazon.de", value: "AMAZON_DE" },
  { label: "amazon.es", value: "AMAZON_ES" },
  { label: "amazon.fr", value: "AMAZON_FR" },
  { label: "amazon.in", value: "AMAZON_IN" },
  { label: "amazon.it", value: "AMAZON_IT" },
  { label: "amazon.co.jp", value: "AMAZON_JP" },
  { label: "amazon.com.mx", value: "AMAZON_MX" },
  { label: "amazon.sa", value: "AMAZON_SA" },
  { label: "amazon.se", value: "AMAZON_SE" },
  { label: "amazon.co.uk", value: "AMAZON_UK" },
  { label: "amazon.ae", value: "AMAZON_AE" },
  { label: "amazon.com.au", value: "AMAZON_AU" },
  { label: "amazon.com.br", value: "AMAZON_BR" },
];

// Order form constants
export const ORDER_FORM_DEFAULTS = {
  name: null,
  state: "INACTIVE",
  budgetCaps: [],
  flights: [
    {
      startDateTime: moment().add(1, "d").startOf("d").toISOString(),
      endDateTime: moment().add(30, "d").endOf("d").toISOString(),
      budgetAmount: "100",
    },
  ],
  optimization: {
    bidStrategy: "SPEND_BUDGET_IN_FULL",
    automateBudgetAllocation: true,
    automatedAdGroupCreationSettings: {
      performancePlus: {},
    },
    goalSetting: {
      goal: "CONVERSION",
      kpi: "RETURN_ON_AD_SPEND",
      targetKpi: "4",
      disableKpiTarget: false,
    },
  },
  enableAutomatedAdGroupCreation: false,
  frequencyCapped: false,
  frequencies: [
    {
      frequencyType: "UNCAPPED",
    },
  ],
  conversionTrackingAsins: [],
  conversionTrackingDomain: ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS[0].value,
};

export const ORDER_STATE_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

export const ORDER_GOAL_OPTIONS = [
  { label: "Awareness", value: "AWARENESS" },
  { label: "Conversions", value: "CONVERSION" },
  { label: "Consideration", value: "CONSIDERATION" },
];

export const ORDER_BID_OPTIMIZATION_STATEGY_OPTIONS = [
  { label: "Spend Budget in Full", value: "SPEND_BUDGET_IN_FULL" },
  { label: "Prioritize KPI Target", value: "PRIORITIZE_KPI_TARGET" },
];

export const ORDER_GOAL_KPI_OPTIONS_BY_GOAL = {
  AWARENESS: [{ label: "Reach", value: "REACH", format: "number" }],
  CONVERSION: [
    { label: "Return on Ad Spend (ROAS)", value: "RETURN_ON_AD_SPEND", format: "number" },
    { label: "Total ROAS (TROAS)", value: "TOTAL_RETURN_ON_AD_SPEND", format: "number" },
    { label: "Cost Per Action (CPA)", value: "COST_PER_ACTION", format: "currency" },
    { label: "Combined ROAS", value: "COMBINED_RETURN_ON_AD_SPEND", format: "number" },
    { label: "Cost Per Sign Up (CPSU)", value: "COST_PER_SIGN_UP", format: "currency" },
    { label: "Cost Per First App Open (CPFAO)", value: "COST_PER_FIRST_APP_OPEN", format: "currency" },
  ],
  CONSIDERATION: [
    { label: "Click Through Rate (CTR)", value: "CLICK_THROUGH_RATE", format: "percent" },
    { label: "Cost Per Click (CPC)", value: "COST_PER_CLICK", format: "currency" },
    {
      label: "Cost Per Video Completion (CPVC)",
      value: "COST_PER_VIDEO_COMPLETION",
      format: "currency",
    },
    { label: "Video Completion Rate (VCR)", value: "VIDEO_COMPLETION_RATE", format: "percent" },
    {
      label: "Cost Per Detail Page View (CPDPV)",
      value: "COST_PER_DETAIL_PAGE_VIEW",
      format: "currency",
    },
    { label: "Detail Page View Rate (DPVR)", value: "DETAIL_PAGE_VIEW_RATE", format: "percent" },
  ],
};

// Line item form constants
export const LINE_ITEM_FORM_DEFAULTS = {
  name: null,
  state: "ACTIVE",
  inventoryType: "DISPLAY",
  bid: {
    baseBid: 0.7,
  },
  optimizeMaxAverageCPM: true,
  startDateTime: moment().add(1, "d").startOf("d").toISOString(),
  endDateTime: moment().add(30, "d").endOf("d").toISOString(),
  automateBudgetAllocation: true,
  pacing: {
    deliveryProfile: "EVEN",
    catchUpBoostPercentage: 0,
  },
  creativeRotationType: "RANDOM",
  frequencyCapped: true,
  targetingSettings: {
    amazonViewability: {
      viewabilityTier: "ALL_TIERS",
      includeUnmeasurableImpressions: false,
    },
    timeZoneType: "VIEWER",
    userLocation: "EVERYWHERE",
    userLocationSignal: "MULTIPLE_SIGNALS",
    enableLanguageTargeting: true,
  },
  budgetCaps: [],
  frequencies: [
    {
      frequencyType: "CUSTOM",
      maxImpressions: 5,
      timeUnitCount: 1,
      timeUnit: "DAYS",
    },
  ],
  optimization: {
    bidStrategy: "PRIORITIZE_KPI_TARGET",
    automateBudgetAllocation: true,
  },
  mobileOperatingSystem: "ALL",
  mediaDevices: ["DESKTOP", "MOBILE"],
  mediaMobileEnvironments: ["APP", "WEB"],
  creativeAssociations: [],
  amazonInventories: [],
  thirdPartyInventories: [],
  amazonPublisherDirectInventories: [],
};

export const LINE_ITEM_STATE_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

export const LINE_ITEM_MAX_AVERAGE_CPM_OPTIMIZATION_OPTIONS = [
  { label: "Automatically Optimize", value: true },
  { label: "Manually Set", value: false },
];

export const LINE_ITEM_BID_OPTIMIZATION_STRATEGY_OPTIONS = [
  { label: "Use Campaign Strategy", value: "USE_CAMPAIGN_STRATEGY" },
  { label: "Spend Budget in Full", value: "SPEND_BUDGET_IN_FULL" },
  { label: "Prioritize KPI Target", value: "PRIORITIZE_KPI_TARGET" },
];

export const LINE_ITEM_DELIVERY_PACING_OPTIONS = [
  { label: "Even", value: "EVEN" },
  { label: "Pace Ahead", value: "PACE_AHEAD" },
  { label: "ASAP", value: "ASAP" },
];

export const LINE_ITEM_CREATIVE_ROTATION_TYPE_OPTIONS = [
  { label: "Random", value: "RANDOM" },
  { label: "Weighted", value: "WEIGHTED" },
];

export const VIDEO_COMPLETION_TIER_OPTIONS = [
  { label: "All Tiers", value: "ALL_TIERS" },
  { label: "Greater Than 10%", value: "GREATER_THAN_10_PERCENT" },
  { label: "Greater Than 20%", value: "GREATER_THAN_20_PERCENT" },
  { label: "Greater Than 30%", value: "GREATER_THAN_30_PERCENT" },
  { label: "Greater Than 40%", value: "GREATER_THAN_40_PERCENT" },
  { label: "Greater Than 50%", value: "GREATER_THAN_50_PERCENT" },
  { label: "Greater Than 60%", value: "GREATER_THAN_60_PERCENT" },
  { label: "Greater Than 70%", value: "GREATER_THAN_70_PERCENT" },
  { label: "Greater Than 80%", value: "GREATER_THAN_80_PERCENT" },
  { label: "Greater Than 90%", value: "GREATER_THAN_90_PERCENT" },
];

export const VIEWABILITY_TIER_OPTIONS = [
  { label: "All Tiers", value: "ALL_TIERS" },
  { label: "Greater Than 70%", value: "GREATER_THAN_70_PERCENT" },
  { label: "Greater Than 60%", value: "GREATER_THAN_60_PERCENT" },
  { label: "Greater Than 50%", value: "GREATER_THAN_50_PERCENT" },
  { label: "Greater Than 40%", value: "GREATER_THAN_40_PERCENT" },
  { label: "Less Than 40%", value: "LESS_THAN_40_PERCENT" },
];

export const TIME_ZONE_TYPE_OPTIONS = [
  { label: "Viewer", value: "VIEWER" },
  { label: "Advertiser Region", value: "ADVERTISER_REGION" },
];

export const USER_LOCATION_OPTIONS = [
  { label: "Everywhere", value: "EVERYWHERE" },
  { label: "Outside of United States", value: "NON_US" },
  { label: "United Arab Emirates", value: "AE" },
  { label: "Australia", value: "AU" },
  { label: "Brazil", value: "BR" },
  { label: "Canada", value: "CA" },
  { label: "Germany", value: "DE" },
  { label: "Spain", value: "ES" },
  { label: "France", value: "FR" },
  { label: "India", value: "IN" },
  { label: "Italy", value: "IT" },
  { label: "Japan", value: "JP" },
  { label: "Mexico", value: "MX" },
  { label: "Netherlands", value: "NL" },
  { label: "Poland", value: "PL" },
  { label: "Saudi Arabia", value: "SA" },
  { label: "Singapore", value: "SG" },
  { label: "Turkey", value: "TR" },
  { label: "United Kingdom", value: "UK" },
  { label: "United States", value: "US" },
  { label: "Ireland", value: "IE" },
];

export const MOBILE_OS_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "IOS", value: "IOS" },
  { label: "Android", value: "ANDROID" },
];

export const DEVICE_OPTIONS = [
  {
    label: "Desktop",
    value: "DESKTOP",
  },
  {
    label: "Mobile",
    value: "MOBILE",
  },
];

export const MOBILE_ENVIRONMENT_OPTIONS = [
  {
    label: "Web",
    value: "WEB",
  },
  { label: "App", value: "APP" },
];

export const PHYSICAL_DEVICE_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "IPhone", value: "IPHONE" },
  { label: "IPad", value: "IPAD" },
  { label: "Android", value: "ANDROID" },
  { label: "Kindle Fire", value: "KINDLE_FIRE" },
  { label: "Kindle Fire HD", value: "KINDLE_FIRE_HD" },
];

export const DEVICE_ORIENTATION_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Portrait", value: "PORTRAIT" },
  { label: "Landscape", value: "LANDSCAPE" },
];

export const USER_LOCATION_SIGNAL_OPTIONS = [
  { label: "Current", value: "CURRENT" },
  { label: "Multiple Signals", value: "MULTIPLE_SIGNALS" },
];

export const LINE_ITEM_CATCH_UP_BOOST_OPTIONS = [
  { label: "No Boost", value: 0 },
  { label: "200% Boost", value: 200 },
  { label: "300% Boost", value: 300 },
  { label: "400% Boost", value: 400 },
];

export const LEGACY_INVENTORY_TYPE_OPTIONS = [
  { label: "Display", value: "DISPLAY" },
  { label: "Standard Display", value: "STANDARD_DISPLAY" },
  { label: "Amazon Mobile Display", value: "AMAZON_MOBILE_DISPLAY" },
  { label: "AAP Mobile App", value: "AAP_MOBILE_APP" },
];

export const INVENTORY_TYPE_OPTIONS = [{ label: "Display", value: "DISPLAY" }];

export const API_FIELD_MAPPINGS = {
  bid: "bid",
  adGroupId: "ad_group_id",
  inventoryType: "inventory_type",
  deliveryStatus: "delivery_status",
  fees: "fees",
  comments: "comments",
  creativeRotationType: "creative_rotation_type",
  advProductCategoryIds: "advertised_product_category_ids",
  budgetCaps: "budget_caps",
  startDateTime: "start_date_time",
  endDateTime: "end_date_time",
  pacing: "pacing",
  targetingSettings: "targeting_settings",
  budgetAmount: "budget_amount",
  optimization: "optimization",
  name: "name",
  state: "state",
  frequencies: "frequencies",
  flights: "flights",
  advertiserId: "advertiser_id",
};

export const MIN_REQUIRED_INVENTORY_TARGETS = 1;
